import React, { FC, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  ButtonGroup,
  Button,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { Save, Close, AddPhotoAlternate, Delete } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { FileDrop } from '../../../../components';
import { IBuildCommunication } from '../../../../models';
import { PhotoUploadHistory } from './photo-upload-history';
import { uploadBuildCommunicationPhoto } from '../../../../fetch';
import { useDropzone } from 'react-dropzone';
import { imageFileTypes } from '../../../../components/file/utils';

interface IPhotoUploadProps {
  buildId: number;
  buildDetails?: IBuildCommunication;
}

export const PhotoUpload: FC<IPhotoUploadProps> = ({ buildId, buildDetails }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 960px)');

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [comment, setComment] = useState<string>('');

  const fileTypes = imageFileTypes.filter(x => x !== '.tiff');

  // Upload mutation
  const uploadMutation = useMutation(
    async (data: { files: File[]; comment: string }) => {
      const formData = new FormData();
      data.files.forEach(file => {
        formData.append('Images', file);
      });
      formData.append('Comment', data.comment);

      return await uploadBuildCommunicationPhoto(buildId, formData);
    },
    {
      onSuccess: () => {
        enqueueSnackbar('Photos uploaded successfully', { variant: 'success' });
        handleCancel(); // Reset form
        // Invalidate the photos query cache to trigger a refresh in PhotoUploadHistory
        queryClient.invalidateQueries(['getBuildCommunicationPhotos', buildId]);
      },
      onError: (error: any) => {
        console.error('Upload error:', error);
        enqueueSnackbar('Error uploading photo. Please try again.', { variant: 'error' });
      }
    }
  );

  // Dropzone configuration
  const dropzoneProps = useDropzone({
    accept: {
      'image/jpg': ['.jpg', '.jpeg'],
      'image/png': [],
      'image/webp': []
    },
    useFsAccessApi: false,
    multiple: true,
    disabled: uploadMutation.isLoading,
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...acceptedFiles]);
      } else {
        enqueueSnackbar(
          `Invalid file type. Please upload one of these image types: ${fileTypes.join(', ')}.`,
          {
            variant: 'error'
          }
        );
      }
    }
  });

  const handleRemoveImage = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveAllImages = () => {
    setSelectedFiles([]);
  };

  const handleSave = async () => {
    if (selectedFiles.length === 0) {
      enqueueSnackbar('Please select at least one image to upload', { variant: 'warning' });
      return;
    }

    uploadMutation.mutate({
      files: selectedFiles,
      comment: comment.trim()
    });
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    setComment('');
  };

  const isSaveDisabled = selectedFiles.length === 0 || uploadMutation.isLoading;

  return (
    <Box className={classes.container}>
      <Grid container spacing={isMobile ? 1 : 3}>
        {/* History Section */}
        <Grid item xs={12} md={6}>
          <PhotoUploadHistory buildId={buildId} />
        </Grid>

        {/* Upload Section */}
        <Grid item xs={12} md={6}>
          <Box className={classes.uploadSection}>
            <Box mb={3}>
              {/* Selected Images Preview */}
              {selectedFiles.length > 0 && (
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Selected Images ({selectedFiles.length})
                    </Typography>
                    <Button
                      size="small"
                      onClick={handleRemoveAllImages}
                      disabled={uploadMutation.isLoading}
                      startIcon={<Delete />}
                    >
                      Remove All
                    </Button>
                  </Box>
                  <Box className={classes.imageGrid}>
                    {selectedFiles.map((file, index) => (
                      <Box key={index} className={classes.imagePreviewItem}>
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Preview ${index + 1}`}
                          className={classes.previewImage}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(index)}
                          className={classes.removeImageButton}
                          disabled={uploadMutation.isLoading}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                        <Typography variant="caption" className={classes.fileName}>
                          {file.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Drop Zone */}
              {selectedFiles.length === 0 && (
                <Box className={classes.imagePreviewContainer}>
                  <Box className={classes.placeholderContainer}>
                    <AddPhotoAlternate className={classes.placeholderIcon} />
                    <Typography variant="body2" color="textSecondary">
                      No images selected
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Drag and Drop Area */}
              <Box mt={1}>
                <FileDrop
                  {...dropzoneProps}
                  title=""
                  id="photo-upload-dropzone"
                  className={uploadMutation.isLoading ? classes.disabledDropzone : ''}
                >
                  <Typography
                    variant="body1"
                    color={uploadMutation.isLoading ? 'textSecondary' : 'textPrimary'}
                  >
                    {uploadMutation.isLoading ? (
                      'Uploading images...'
                    ) : (
                      <>
                        Drag and drop images or{' '}
                        <Box
                          component="a"
                          href="/#"
                          sx={{
                            color: theme => theme.palette.primary.main,
                            textDecoration: 'none',
                            pointerEvents: uploadMutation.isLoading ? 'none' : 'auto'
                          }}
                          onClick={e => e.preventDefault()}
                        >
                          browse and pick
                        </Box>{' '}
                      </>
                    )}
                  </Typography>
                </FileDrop>
              </Box>
            </Box>

            <Box mb={2}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Add a Comment"
                placeholder="Type here"
                value={comment}
                onChange={e => setComment(e.target.value)}
                disabled={uploadMutation.isLoading}
              />
            </Box>

            <Box marginTop="1rem">
              <ButtonGroup>
                <Button
                  disabled={isSaveDisabled}
                  type="button"
                  onClick={handleSave}
                  startIcon={<Save />}
                  variant="contained"
                  color="primary"
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<Close />}
                  color="inherit"
                  onClick={handleCancel}
                  disabled={uploadMutation.isLoading}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(1),
    minHeight: '600px',
    [theme.breakpoints.down('md')]: {
      padding: 0
    }
  },
  uploadSection: {
    backgroundColor: '#fff',
    padding: theme.spacing(0.25),
    paddingLeft: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    borderLeft: `1px solid ${theme.palette.grey[300]}`,
    [theme.breakpoints.down('md')]: {
      borderLeft: 'none'
    }
  },
  imagePreviewContainer: {
    width: '100%',
    height: '275px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    position: 'relative'
  },
  imageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: theme.spacing(1),
    maxHeight: '200px',
    overflowY: 'auto'
  },
  imagePreviewItem: {
    position: 'relative',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: '#f5f5f5'
  },
  previewImage: {
    width: '100%',
    height: '100px',
    objectFit: 'cover',
    backgroundColor: '#f5f5f5'
  },
  removeImageButton: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '2px',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)'
    }
  },
  fileName: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: theme.spacing(0.25, 0.5),
    fontSize: '0.7rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  dropZoneOverlay: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[50],
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius
  },
  placeholderContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100]
  },
  placeholderIcon: {
    fontSize: '3rem',
    color: theme.palette.grey[400],
    marginBottom: theme.spacing(1)
  },
  disabledDropzone: {
    opacity: 0.6,
    cursor: 'not-allowed',
    pointerEvents: 'none'
  }
}));
